import { ArrowRight, Check, LockKeyhole, Trash2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { CheckoutSteps } from "../components/brand";
import { QuantityStepper } from "../components/QuantityStepper";
import { Seo } from "../components/Seo";
import { formatMoney } from "../lib/api";
import { useCart } from "../state/cart";

export function CartPage() {
  const cart = useCart();
  const location = useLocation();
  const addedId = (location.state as { addedId?: string } | null)?.addedId;

  if (!cart.items.length) return (
    <section className="container-page py-20 text-center md:py-28">
      <Seo title="Bag" path="/cart" noindex />
      <EmptyShelf />
      <h1 className="mt-8 font-display text-4xl text-cocoa">Your bag is empty</h1>
      <p className="mx-auto mt-3 max-w-[36ch] text-muted">There’s room on the shelf for something tiny.</p>
      <Link className="btn-primary mt-8" to="/shop">Browse the shop</Link>
    </section>
  );

  return (
    <section className="container-page pb-10 pt-8 md:pt-12">
      <Seo title="Bag" path="/cart" noindex />
      <CheckoutSteps current={0} />
      <h1 className="mt-6 font-display text-[2.5rem] leading-none text-cocoa md:text-5xl">Your bag</h1>

      <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
        <ul className="divide-y divide-line border-y border-line lg:col-span-8">
          {cart.items.map((item) => <li key={item.id} className={`grid grid-cols-[84px_1fr] gap-4 py-5 sm:grid-cols-[112px_1fr] sm:gap-6 ${item.id === addedId ? "just-added -mx-3 rounded-[6px] px-3" : ""}`}>
            <Link to={`/products/${item.slug}`} tabIndex={-1} aria-hidden="true"><img src={item.imageUrl} alt="" className="aspect-square w-full rounded-[4px] bg-frosting object-cover" /></Link>
            <div className="min-w-0">
              {item.id === addedId && <p className="mb-1 flex items-center gap-1 text-[13px] font-bold text-mint"><Check size={14} /> Added to your bag</p>}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/products/${item.slug}`} className="font-bold leading-snug text-cocoa hover:text-ribbon-ink">{item.name}</Link>
                  {item.variant && <p className="text-sm text-muted">{item.variant.name}</p>}
                </div>
                <p className="shrink-0 font-bold tabular-nums">{formatMoney(item.unitPriceCentavos * item.quantity)}</p>
              </div>
              {(item.addons.length > 0 || item.customizations.length > 0 || item.referenceFiles.length > 0) && <ul className="mt-2 space-y-0.5 text-[13px] text-muted">
                {item.addons.map((addon) => <li key={addon.id}>+ {addon.name}</li>)}
                {item.customizations.map((custom) => <li key={custom.fieldId} className="truncate">{custom.label}: {Array.isArray(custom.value) ? custom.value.join(", ") : String(custom.value)}</li>)}
                {item.referenceFiles.length > 0 && <li>{item.referenceFiles.length} reference photo{item.referenceFiles.length === 1 ? "" : "s"}</li>}
              </ul>}
              <div className="mt-3 flex items-center justify-between gap-3">
                <QuantityStepper size="sm" value={item.quantity} onChange={(quantity) => cart.updateQuantity(item.id, quantity)} />
                <button onClick={() => cart.remove(item.id)} aria-label={`Remove ${item.name}`} className="flex min-h-10 items-center gap-1.5 rounded-full px-3 text-sm font-bold text-muted transition hover:bg-frosting hover:text-cocoa"><Trash2 size={15} /> Remove</button>
              </div>
            </div>
          </li>)}
        </ul>

        <aside className="h-fit rounded-xl bg-frosting p-6 lg:sticky lg:top-24 lg:col-span-4">
          <h2 className="text-lg font-bold">Summary</h2>
          <dl className="mt-4 space-y-3 text-[15px]">
            <div className="flex justify-between"><dt className="text-muted">Subtotal, {cart.count} item{cart.count === 1 ? "" : "s"}</dt><dd className="font-bold tabular-nums">{formatMoney(cart.subtotalCentavos)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Pickup or delivery</dt><dd className="text-muted">Next step</dd></div>
          </dl>
          <Link to="/checkout" className="btn-primary mt-6 w-full">Check out <ArrowRight size={18} /></Link>
          <p className="mt-4 flex gap-2 text-[13px] leading-relaxed text-muted"><LockKeyhole size={15} className="mt-0.5 shrink-0" /> Prices are checked again and locked in when you place your order.</p>
          <Link to="/shop" className="link-quiet mt-5 text-sm">Keep shopping</Link>
        </aside>
      </div>
    </section>
  );
}

/** A small, empty pink cubby shelf, the same object as the homepage hero. */
function EmptyShelf() {
  return (
    <div className="mx-auto w-32 rounded-[4px] bg-ribbon p-[6px]" aria-hidden="true">
      <div className="grid grid-cols-2 gap-[6px]">
        {[0, 1, 2, 3].map((n) => <span key={n} className="aspect-square rounded-[1px] bg-frosting shadow-[inset_0_8px_8px_-6px_rgba(69,35,41,.35)]" />)}
      </div>
    </div>
  );
}
